<!-- Hero Section with Registration Form -->
<section class="hero">
  <div class="background">
    <div class="overlay"></div>
  </div>
  <div class="decorations">
    <div class="circle top-right"></div>
    <div class="circle bottom-left"></div>
    <div class="circle center"></div>
  </div>
  <div class="container">
    <mat-card class="registration-card">
      <div class="registration-card__brand">
        <div class="registration-card__brand-icon"><mat-icon>point_of_sale</mat-icon></div>
        <span>Nova Point</span>
      </div>
      <mat-card-header class="full-width">
        <mat-card-title>Create Admin Account</mat-card-title>
        <mat-card-subtitle>{{ currentStep === 1 ? 'Step 1: Your Details' : 'Step 2: Shop Details' }}</mat-card-subtitle>
      </mat-card-header>

      <!-- Step Indicator -->
      <div class="step-indicator">
        <div class="step" [class.active]="currentStep >= 1" [class.completed]="currentStep > 1">
          <div class="step-circle">1</div>
          <span class="step-label">Account</span>
        </div>
        <div class="step-line" [class.active]="currentStep > 1"></div>
        <div class="step" [class.active]="currentStep >= 2">
          <div class="step-circle">2</div>
          <span class="step-label">Shop</span>
        </div>
      </div>

      <mat-card-content>
        <form [formGroup]="registrationForm" (ngSubmit)="onSubmit()" class="form mt-4">

          <!-- Step 1: User Details -->
          <div *ngIf="currentStep === 1" class="step-content">
            <div class="full-width">
              <mat-form-field appearance="outline">
                <mat-label>Username</mat-label>
                <input matInput formControlName="username" placeholder="Choose a username" required />
                <mat-icon matPrefix>account_circle</mat-icon>
                <span matSuffix class="username-status">
                  <mat-spinner *ngIf="checkingUsername" diameter="18"></mat-spinner>
                  <mat-icon *ngIf="!checkingUsername && usernameAvailable === true" class="available">check_circle</mat-icon>
                  <mat-icon *ngIf="!checkingUsername && usernameAvailable === false" class="taken">cancel</mat-icon>
                </span>
                <mat-error *ngIf="registrationForm.get('username')?.hasError('required')">Username is required</mat-error>
                <mat-error *ngIf="registrationForm.get('username')?.hasError('minlength')">Minimum 3 characters</mat-error>
              </mat-form-field>
              <div class="username-hint available" *ngIf="!checkingUsername && usernameAvailable === true">
                Username is available
              </div>
              <div class="username-hint taken" *ngIf="!checkingUsername && usernameAvailable === false">
                Username is already taken
              </div>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>First Name</mat-label>
                <input matInput formControlName="firstName" placeholder="Enter your first name" required />
                <mat-icon matPrefix>person</mat-icon>
                <mat-error *ngIf="registrationForm.get('firstName')?.hasError('required')">First name is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Name</mat-label>
                <input matInput formControlName="lastName" placeholder="Enter your last name" required />
                <mat-icon matPrefix>person_outline</mat-icon>
                <mat-error *ngIf="registrationForm.get('lastName')?.hasError('required')">Last name is required</mat-error>
              </mat-form-field>
            </div>

            <div class="full-width">
              <mat-form-field appearance="outline">
                <mat-label>Email Address</mat-label>
                <input matInput type="email" formControlName="email" placeholder="you@example.com" required />
                <mat-icon matPrefix>email</mat-icon>
                <mat-error *ngIf="registrationForm.get('email')?.hasError('required')">Email is required</mat-error>
                <mat-error *ngIf="registrationForm.get('email')?.hasError('email')">Enter a valid email</mat-error>
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Password</mat-label>
                <input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="password" placeholder="Create a password" required />
                <mat-icon matPrefix>lock</mat-icon>
                <button mat-icon-button matSuffix type="button" (click)="hidePassword = !hidePassword">
                  <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-error *ngIf="registrationForm.get('password')?.hasError('required')">Password is required</mat-error>
                <mat-error *ngIf="registrationForm.get('password')?.hasError('minlength')">Minimum 8 characters</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Confirm Password</mat-label>
                <input matInput [type]="hideConfirmPassword ? 'password' : 'text'" formControlName="confirmPassword" placeholder="Confirm your password" required />
                <mat-icon matPrefix>lock_outline</mat-icon>
                <button mat-icon-button matSuffix type="button" (click)="hideConfirmPassword = !hideConfirmPassword">
                  <mat-icon>{{ hideConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-error *ngIf="registrationForm.get('confirmPassword')?.hasError('required')">Please confirm password</mat-error>
              </mat-form-field>
            </div>

            <div class="password-mismatch" *ngIf="registrationForm.get('confirmPassword')?.touched && !passwordsMatch">
              Passwords do not match
            </div>

            <button
              mat-raised-button
              type="button"
              (click)="nextStep()"
              class="submit-button"
            >
              Next: Shop Details
              <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>

          <!-- Step 2: Shop Details -->
          <div *ngIf="currentStep === 2" class="step-content">
            <div class="full-width">
              <mat-form-field appearance="outline">
                <mat-label>Shop Name</mat-label>
                <input matInput formControlName="shopName" placeholder="Enter your shop name" required />
                <mat-icon matPrefix>store</mat-icon>
                <mat-error *ngIf="registrationForm.get('shopName')?.hasError('required')">Shop name is required</mat-error>
              </mat-form-field>
            </div>

            <div class="full-width">
              <mat-form-field appearance="outline">
                <mat-label>Referral Code (optional)</mat-label>
                <input matInput formControlName="referralCode" placeholder="e.g. MAINSHOP001" />
                <mat-icon matPrefix>redeem</mat-icon>
              </mat-form-field>
            </div>

            <div class="full-width">
              <mat-form-field appearance="outline">
                <mat-label>Shop Address</mat-label>
                <input matInput formControlName="shopAddress" placeholder="123 Main St, City" required />
                <mat-icon matPrefix>location_on</mat-icon>
                <mat-error *ngIf="registrationForm.get('shopAddress')?.hasError('required')">Shop address is required</mat-error>
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Shop Phone</mat-label>
                <input matInput type="tel" formControlName="shopPhone" placeholder="+255123456789" required />
                <mat-icon matPrefix>phone</mat-icon>
                <mat-error *ngIf="registrationForm.get('shopPhone')?.hasError('required')">Shop phone is required</mat-error>
                <mat-error *ngIf="registrationForm.get('shopPhone')?.hasError('pattern')">Enter a valid phone number</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Shop Email</mat-label>
                <input matInput type="email" formControlName="shopEmail" placeholder="shop@example.com" required />
                <mat-icon matPrefix>mail_outline</mat-icon>
                <mat-error *ngIf="registrationForm.get('shopEmail')?.hasError('required')">Shop email is required</mat-error>
                <mat-error *ngIf="registrationForm.get('shopEmail')?.hasError('email')">Enter a valid email</mat-error>
              </mat-form-field>
            </div>

            <div class="full-width terms-checkbox">
              <mat-checkbox formControlName="agreeToTerms">
                I agree to the
                <a routerLink="/terms-of-service" target="_blank">Terms of Service</a>
                and
                <a routerLink="/privacy-policy" target="_blank">Privacy Policy</a>
              </mat-checkbox>
              <mat-error *ngIf="registrationForm.get('agreeToTerms')?.touched && registrationForm.get('agreeToTerms')?.hasError('required')">
                You must agree to continue
              </mat-error>
            </div>

            <div class="button-row">
              <button
                mat-raised-button
                type="button"
                (click)="previousStep()"
                class="step-back-btn"
              >
                <mat-icon>arrow_back</mat-icon>
                Back
              </button>

              <button
                mat-raised-button
                type="submit"
                [disabled]="registrationForm.invalid || loading || !passwordsMatch"
                class="submit-button"
              >
                <mat-icon *ngIf="!loading">person_add</mat-icon>
                <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
                {{ loading ? 'Creating Account...' : 'Create Account' }}
              </button>
            </div>
          </div>

          <!-- Login Link -->
          <div class="login-link">
            <p>Already have an account? <a (click)="openLoginModal()">Sign in</a></p>
          </div>
        </form>
      </mat-card-content>
    </mat-card>
  </div>
</section>
